
import React, { useState, useEffect } from "react";
import defaultExhibitionImage from "../../Assets/images/default-exhibition.jpg"; // Assume you have a default image
import { useNavigate } from "react-router-dom";
import { getExhibitions } from "./request"; // Adjust import based on your request file
import { websiteUrl } from "../../const/constants";

const toUrduDigits = (num) => {
  return num?.toString().replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]) || "";
};

const ExhibitionHighlights = ({ exhibition }) => {
  const [exhibitionData, setExhibitionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const fetchExhibitionData = async () => {
    setLoading(true);
    try {
      const response = await getExhibitions();
      if (response.Result) {
        const latestExhibition = response.Exhibitions.reduce((latest, current) => {
          return parseInt(current.year) >
            parseInt(latest?.year || 0)
            ? current
            : latest;
        }, null);
        setExhibitionData(latestExhibition || null);
      }
    } catch (error) {
      console.error("Error fetching exhibition data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!exhibition) {
      fetchExhibitionData();
    }
  }, [exhibition]);

  const selectedExhibition = exhibition || exhibitionData;

  const handleSeeMoreExhibition = () => {
    navigate("/munasibat/exhibition"); // Adjust route as needed
  };

  const getHijriYearRange = (gregorianYear) => {
    if (!gregorianYear) return "۱۴۴۷-۱۴۴۶";
    const hijriYear =
      parseInt(gregorianYear) -
      622 +
      Math.floor((parseInt(gregorianYear) - 622) / 32);
    return `${toUrduDigits(hijriYear)}-${toUrduDigits(hijriYear + 1)}`;
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return defaultExhibitionImage;
    if (imagePath.startsWith("http")) return imagePath;
    return `${websiteUrl}/exhibitionImages/${imagePath.replace(/^\//, "")}`;
  };

  const getPdfUrl = (pdfPath) => {
    if (!pdfPath) return null;
    if (pdfPath.startsWith("http")) return pdfPath;
    return `${websiteUrl}/exhibitionPdf/${pdfPath.replace(/^\//, "")}`;
  };

  const handleViewPdf = () => {
    if (selectedExhibition?.pdf) {
      setIsModalOpen(true);
    }
  };

  const handleDownloadPdf = async () => {
    if (selectedExhibition?.pdf) {
      try {
        const pdfUrl = getPdfUrl(selectedExhibition.pdf);
        const response = await fetch(pdfUrl, {
          method: "GET",
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.statusText}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Exhibition_${selectedExhibition.year}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url); // Clean up
      } catch (error) {
        console.error("Error downloading PDF:", error);
        // Optional: Show user-friendly error message, e.g., alert('Failed to download PDF. Please try again.');
      }
    }
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };

  if (loading || !selectedExhibition) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-primary border-solid"></div>
      </div>
    );
  }

  const hijriYears = getHijriYearRange(selectedExhibition?.year);
  const pdfUrl = getPdfUrl(selectedExhibition?.pdf);

  return (
    <div className=" rounded-3xl">
      <button
        onClick={handleSeeMoreExhibition}
        className="flex flex-col-reverse lg:flex-row-reverse items-center justify-between gap-6 w-full h-full"
      >
        {/* Text Content */}
        <div className="text-right flex flex-col py-8 sm:py-10 md:py-12 px-5 sm:px-10 md:px-10 lg:px-[4rem]">
          <div>
            <p
              dir="ltr"
              className="font-nastaleeqKasheeda text-2xl sm:text-3xl md:text-2xl lg:text-4xl mb-4 max-w-[470px] mx-auto lg:mx-0"
            >
              {hijriYears}&#8202;&#8202;&#8202;&#8202;&#8202;&#8202;ایگزیبیشن
            </p>
            <p className="tracking-[0.1rem] text-lg sm:text-xl md:text-xl lg:text-2xl font-nastaleeqRegular max-w-[470px] mx-auto lg:mx-0">
              {/* Add your exhibition-specific description here */}
              ایسوسی ایشن آف مشتاقانِ نور ہر سال تعارف امام زمانہ علیہ السلام کے
              عنوان سے ایگزیبیشن کا اجرا کرتی ہے۔ الحمد للہ یہ کام گزشتہ کئی
              سالوں سے برادران کے علمی مطالعہ اور کوششوں سے جاری ہے۔
              <br></br>
              ایگزیبیشن کا مقصد و ہدف ان عام ایگزیبیشن سے مختلف ہے جو صرف روز مرہ کی ضروری باتیں نوٹ کرنے کے لئے شائع کی جاتی ہیں۔ 
               ایگزیبیشن کے دو بڑے مقاصد ہیں اولا محاسبہ نفس اور ثانیا معرفت امام زمانہ علیہ السلام۔
            </p>
          </div>
          {selectedExhibition?.pdf && (
            <div className="flex justify-end gap-5 mt-4">
              <button
                onClick={handleViewPdf}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-all duration-300"
              >
                View PDF
              </button>
              <button
                onClick={handleDownloadPdf}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-secondary-dark transition-all duration-300"
              >
                Download PDF
              </button>
            </div>
          )}
        </div>
        {/* Image */}
        <div className="w-full    lg:w-1/2 flex justify-center lg:justify-start ">
          <img
            src={getImageUrl(selectedExhibition?.image)}
            alt={`Exhibition ${
              toUrduDigits(selectedExhibition?.year) || "\u06f2\u06f0\u06f2\u06f5"
            }`}
            className="max-h-40 md:max-h-72 object-contain rounded-lg md:mr-10 "
            onError={(e) => {
              e.target.src = defaultExhibitionImage;
            }}
            loading="lazy"
          />
        </div>
      </button>

      {/* PDF Modal */}
      {isModalOpen && pdfUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-11/12 md:w-3/4 lg:w-1/2 max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-xl font-nastaleeqKasheeda">
                ایگزیبیشن {toUrduDigits(selectedExhibition?.year)}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                &times;
              </button>
            </div>
            <div className="p-4">
              <iframe
                src={pdfUrl}
                className="w-full h-[70vh]"
                title={`Exhibition ${selectedExhibition?.year} PDF`}
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExhibitionHighlights;